import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { trigger, style, transition, animate } from '@angular/animations';
import { DOCUMENT, NgClass } from '@angular/common';

@Component({
  selector: 'app-modal',
  standalone: true,
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
  imports: [NgClass],
  animations: [
    // Backdrop fade
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0 })),
      ]),
    ]),

    // Modal animation that adapts to the layout (center vs right panel)
    trigger('modalVariant', [
      // Centered modal (default)
      transition('void => center', [
        // Start slightly below & faded
        style({ transform: 'translateX(-50%) translateY(calc(-50% + 30px))', opacity: 0 }),
        animate('250ms cubic-bezier(0.25, 1, 0.5, 1)', style({ transform: 'translate(-50%, -50%)', opacity: 1 })),
      ]),
      transition('center => void', [
        animate('200ms ease-in', style({ transform: 'translateX(-50%) translateY(calc(-50% + 30px))', opacity: 0 })),
      ]),

      // Right panel modal (when .modal--panel-right is applied)
      transition('void => right', [
        // Slide in from the right & fade
        style({ transform: 'translateX(24px)', opacity: 0 }),
        animate('250ms cubic-bezier(0.25, 1, 0.5, 1)', style({ transform: 'none', opacity: 1 })),
      ]),
      transition('right => void', [
        animate('200ms ease-in', style({ transform: 'translateX(24px)', opacity: 0 })),
      ]),
    ]),
  ]
})
export class ModalComponent implements OnChanges, OnDestroy {
  @Input() open = false;
  @Input() showClose = true;

  // Keep your flexible API for classes
  private _modalClass: string | string[] | Set<string> | { [klass: string]: any } = '';
  @Input() set modalClass(value: string | string[] | Set<string> | { [klass: string]: any }) {
    this._modalClass = value;
    // Recompute the "panel-right" flag whenever classes change
    this.isPanelRight = this.hasClass('modal--panel-right', value);
  }
  get modalClass() { return this._modalClass; }

  @Output() closed = new EventEmitter<void>();

  // Derived state used by the animation trigger
  public isPanelRight = false;
  private static openModalCount = 0;
  private static initialBodyOverflow: string | null = null;
  private lockedBodyScroll = false;

  public constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']) {
      this.syncBodyScrollLock(Boolean(changes['open'].currentValue));
    }
  }

  public ngOnDestroy(): void {
    if (this.lockedBodyScroll) {
      this.syncBodyScrollLock(false);
    }
  }

  close() {
    if (this.lockedBodyScroll) {
      this.syncBodyScrollLock(false);
    }
    this.closed.emit();
  }

  // Utility to support all NgClass input shapes
  private hasClass(target: string, value: typeof this._modalClass): boolean {
    if (!value) return false;
    if (typeof value === 'string') return value.split(/\s+/).includes(target);
    if (Array.isArray(value)) return value.includes(target);
    if (value instanceof Set) return value.has(target);
    return !!value[target];
  }

  private syncBodyScrollLock(shouldLock: boolean): void {
    if (!this.document?.body) {
      return;
    }

    if (shouldLock) {
      if (this.lockedBodyScroll) {
        return;
      }

      if (ModalComponent.openModalCount === 0) {
        ModalComponent.initialBodyOverflow = this.document.body.style.overflow;
        this.document.body.style.overflow = 'hidden';
      }

      ModalComponent.openModalCount += 1;
      this.lockedBodyScroll = true;
      return;
    }

    if (!this.lockedBodyScroll) {
      return;
    }

    ModalComponent.openModalCount = Math.max(ModalComponent.openModalCount - 1, 0);

    if (ModalComponent.openModalCount === 0) {
      this.document.body.style.overflow = ModalComponent.initialBodyOverflow ?? '';
      ModalComponent.initialBodyOverflow = null;
    }

    this.lockedBodyScroll = false;
  }
}
